// Changes a staff account asks for, and an admin decides on.
//
// Placements and group changes made by staff never touch the live schedule.
// They become proposals, held server-side (kac_approvals.json), and shown to
// everyone with a "waiting for approval" marker until an admin approves or
// rejects one. Approving applies it through exactly the same functions a
// hand edit uses — see applyApproval() — so there is one code path for
// changing the schedule, not two.
//
// A proposal is:
//   { id, type, payload, summary, status, requestedBy, requestedById,
//     requestedAt, decidedBy, decidedAt, reason }
//
// Five types, matching the gated actions:
//   place         { playerId, toGroupId, fromGroupId, mode: "move" | "add" }
//   remove        { playerId, groupId }
//   create-group  { group }
//   update-group  { groupId, updates }
//   delete-group  { groupId }
//
// Pending proposals hold nothing: a group with three members and one pending
// placement still reads 3/4, and two people may propose the same seat. The
// admin sorts that out — see approvalBlocker() for what can't be applied.

export const GATED_TYPES = ["place", "remove", "create-group", "update-group", "delete-group"];

const nameOf = (players, id) => players.find(p => p.id === id)?.name || id;

// The stored field name is a programmer's word; a request is read by whoever
// approves it. Mirrors kac_field_label() in public/api.php.
const FIELD_LABELS = {
  day: "day",
  startTime: "start time",
  duration: "length",
  court: "court",
  coachId: "coach",
  tier: "level",
  notes: "notes",
  courtType: "court type",
};

function describeUpdates(updates = {}) {
  const parts = Object.keys(updates)
    .filter(k => k !== "waitlist")
    .map(k => `${FIELD_LABELS[k] || k} to ${updates[k] === "" || updates[k] == null ? "(empty)" : updates[k]}`);
  return parts.length > 0 ? parts.join(", ") : "nothing";
}

/**
 * What this request asks for, in a sentence, written when it's made — so it
 * still reads correctly later even if the group it names has since gone.
 */
export function summarizeRequest(type, payload, players = [], groups = []) {
  const who = payload.playerId ? nameOf(players, payload.playerId) : "";
  switch (type) {
    case "place":
      if (payload.mode === "add") return `add ${who} to ${payload.toGroupId} as a second clinic`;
      return payload.fromGroupId
        ? `move ${who} from ${payload.fromGroupId} to ${payload.toGroupId}`
        : `place ${who} in ${payload.toGroupId}`;
    case "remove":
      return `take ${who} out of ${payload.groupId}`;
    case "create-group": {
      const g = payload.group || {};
      const when = g.day && g.startTime ? `${g.day} at ${g.startTime}` : "no time set yet";
      return `create a ${g.tier || "new"} group — ${when}`;
    }
    case "update-group":
      return `change ${payload.groupId}: ${describeUpdates(payload.updates)}`;
    case "delete-group": {
      const size = (groups.find(g => g.id === payload.groupId)?.playerIds || []).length;
      return `delete ${payload.groupId}${size > 0 ? ` (${size} member${size !== 1 ? "s" : ""})` : ""}`;
    }
    default:
      return type;
  }
}

/**
 * Why this request can't be applied as it stands, or null if it can. The
 * schedule moves on while a request waits, so by the time an admin gets to it
 * the group may be gone or the member may already be where it wanted them.
 */
export function approvalBlocker(request, players = [], groups = []) {
  const { type, payload = {} } = request;
  const player = payload.playerId ? players.find(p => p.id === payload.playerId) : null;
  const groupOf = id => groups.find(g => g.id === id);

  if (payload.playerId && !player) return "That member is no longer on the roster.";

  switch (type) {
    case "place": {
      const target = groupOf(payload.toGroupId);
      if (!target) return `${payload.toGroupId} no longer exists.`;
      if ((target.playerIds || []).includes(payload.playerId)) {
        return `${player.name} is already in ${payload.toGroupId}.`;
      }
      return null;
    }
    case "remove": {
      const group = groupOf(payload.groupId);
      if (!group) return `${payload.groupId} no longer exists.`;
      if (!(group.playerIds || []).includes(payload.playerId)) {
        return `${player.name} is no longer in ${payload.groupId}.`;
      }
      return null;
    }
    case "update-group":
    case "delete-group":
      return groupOf(payload.groupId) ? null : `${payload.groupId} no longer exists.`;
    case "create-group":
      return null;
    default:
      return "Unknown change type.";
  }
}

/**
 * Apply an approved request, through the same functions a hand edit uses.
 * `ops` is the set from useKACState.
 */
export function applyApproval(request, ops) {
  const { type, payload = {} } = request;
  switch (type) {
    case "place":
      if (payload.mode === "add") ops.addToGroup(payload.playerId, payload.toGroupId);
      else ops.movePlayer(payload.playerId, payload.fromGroupId ?? null, payload.toGroupId);
      return;
    case "remove":
      ops.removeFromGroup(payload.playerId, payload.groupId);
      return;
    case "create-group":
      ops.createGroup(payload.group || {});
      return;
    case "update-group":
      ops.updateGroup(payload.groupId, payload.updates || {});
      return;
    case "delete-group":
      ops.deleteGroup(payload.groupId);
      return;
    default:
      throw new Error(`Unknown change type: ${type}`);
  }
}

/**
 * Pending requests arranged for the views that draw them in place:
 *
 *   addsByGroup     — someone proposed for this group, shown as a ghost row
 *   removalsByGroup — someone proposed to leave it (a move counts at both ends)
 *   groupChanges    — a reschedule or deletion proposed for this group
 *   newGroups       — proposed groups, which have nowhere to sit but the tab
 *   byPlayer        — every request touching this member, for their card
 */
export function pendingOverlay(requests = []) {
  const overlay = { addsByGroup: {}, removalsByGroup: {}, groupChanges: {}, newGroups: [], byPlayer: {} };
  const push = (bucket, key, req) => {
    if (!key) return;
    (bucket[key] = bucket[key] || []).push(req);
  };

  requests.filter(r => (r.status || "pending") === "pending").forEach(req => {
    const p = req.payload || {};
    push(overlay.byPlayer, p.playerId, req);
    switch (req.type) {
      case "place":
        push(overlay.addsByGroup, p.toGroupId, req);
        // A move empties a seat as well as filling one; a second clinic
        // doesn't, so only the move side is marked as leaving.
        if (p.mode !== "add") push(overlay.removalsByGroup, p.fromGroupId, req);
        break;
      case "remove":
        push(overlay.removalsByGroup, p.groupId, req);
        break;
      case "update-group":
      case "delete-group":
        push(overlay.groupChanges, p.groupId, req);
        break;
      case "create-group":
        overlay.newGroups.push(req);
        break;
      default:
        break;
    }
  });
  return overlay;
}

// True once this member has a pending placement into, or departure from, this
// group — so a view can mark them without digging through the overlay.
export function pendingFor(overlay, groupId) {
  return {
    adds: overlay.addsByGroup[groupId] || [],
    removals: overlay.removalsByGroup[groupId] || [],
    changes: overlay.groupChanges[groupId] || [],
  };
}
